<template>
  <div>
    <div
      class="tc-attribution-event-meta d-flex align-items-center justify-content-between"
      style="padding:var(--s-8) var(--s-12);border-bottom:1px solid var(--stroke-1);background:var(--bg-subtle)"
    >
      <span>{{ contextLabel }}</span>
      <button
        v-if="closeable"
        type="button"
        class="tc-btn-icon"
        style="width:20px;height:20px"
        aria-label="Close"
        @click="emit('close')"
      >
        <svg viewBox="0 0 18 18" fill="none" aria-hidden="true">
          <path d="M13.5 4.5L4.5 13.5M4.5 4.5l9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </button>
    </div>

    <div style="padding:var(--s-8) var(--s-12);border-bottom:1px solid var(--stroke-1)">
      <div style="position:relative;display:flex;align-items:center">
        <svg style="position:absolute;left:8px;color:var(--fg-tertiary);pointer-events:none;width:14px;height:14px" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <circle cx="7" cy="7" r="4.5" stroke="currentColor" stroke-width="1.3"/>
          <path d="M10.5 10.5L13 13" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
        </svg>
        <input
          class="form-control form-control-sm"
          style="padding-left:28px"
          type="text"
          placeholder="Search project…"
          readonly
        />
      </div>
    </div>

    <div class="tc-attribution-label" style="padding:var(--s-4) var(--s-12) var(--s-2);font-variant:small-caps;text-transform:uppercase;letter-spacing:0.06em">Recent</div>

    <div v-for="p in RECENT_PROJECTS" :key="p.code">
      <div
        class="tc-cell-popover-row"
        :class="{ selected: p.selected }"
      >
        <span style="width:16px;color:var(--brand-primary);font-size:13px;text-align:center;flex-shrink:0">{{ p.selected ? '✓' : '' }}</span>
        <div style="flex:1;min-width:0">
          <span class="tc-preview-wbs-code txt-mono d-block">{{ p.code }}</span>
          <span style="font-size:var(--t-13);color:var(--fg-primary);display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{{ p.name }}</span>
        </div>
        <button
          v-if="p.selected"
          type="button"
          class="tc-btn-icon"
          style="width:20px;height:20px;font-size:13px"
          :class="{ 'bg-muted': reasoningOpen }"
          :style="reasoningOpen ? 'color:var(--brand-primary);background:var(--bg-muted)' : ''"
          :title="reasoningOpen ? 'Hide reasoning' : 'Show reasoning'"
          :aria-label="reasoningOpen ? 'Hide reasoning' : 'Show reasoning'"
          @click.stop="reasoningOpen = !reasoningOpen"
        >ⓘ</button>
      </div>

      <div v-if="p.selected && reasoningOpen" class="tc-cell-popover-reasoning">
        <div class="tc-cell-popover-reasoning-heading">Picked because</div>
        <ul class="tc-cell-popover-reasoning-bullets">
          <li>"Acme Corp" matches this client</li>
          <li>5× similar this period</li>
          <li>Recent @acme.com attendees</li>
        </ul>
        <div class="tc-cell-popover-reasoning-heading">Less sure about</div>
        <ul class="tc-cell-popover-reasoning-bullets">
          <li>Hours — Freshdesk touch was estimated</li>
        </ul>
        <div class="tc-cell-popover-reasoning-heading">Also considered</div>
        <ul class="tc-cell-popover-reasoning-bullets">
          <li>2024-ACME-002 · Impl.</li>
          <li>2024-ACME-001 · Concept</li>
        </ul>
      </div>
    </div>

    <hr style="border:none;border-top:1px solid var(--stroke-1);margin:0" />

    <div class="tc-attribution-label" style="padding:var(--s-4) var(--s-12) var(--s-2);font-variant:small-caps;text-transform:uppercase;letter-spacing:0.06em">Other</div>

    <div
      v-for="p in OTHER_PROJECTS"
      :key="p.code"
      class="tc-cell-popover-row"
    >
      <span style="width:16px;flex-shrink:0"></span>
      <div style="flex:1;min-width:0">
        <span class="tc-preview-wbs-code txt-mono d-block">{{ p.code }}</span>
        <span style="font-size:var(--t-13);color:var(--fg-primary);display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{{ p.name }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';

interface Project {
  code: string;
  name: string;
  selected?: boolean;
}

defineProps<{
  contextLabel?: string;
  closeable?: boolean;
}>();

const emit = defineEmits<{
  close: [];
}>();

const reasoningOpen = ref(false);

const RECENT_PROJECTS: Project[] = [
  { code: '2024-ACME-001', name: 'Support', selected: true },
  { code: '2024-NW-002', name: 'Concept' },
  { code: 'OH-MTG', name: 'Internal meetings' },
];

const OTHER_PROJECTS: Project[] = [
  { code: '2024-ACME-002', name: 'Implementation' },
  { code: '2024-OH-PROD', name: 'Admin' },
];
</script>
